/**
 * ModuleContext.tsx
 *
 * React Context for managing enabled modules state across the EMS application.
 * Provides centralized access to which modules are enabled and automatically
 * refreshes when modules are toggled.
 *
 * Features:
 * - Fetches enabled modules from API on mount
 * - Listens for 'ems:modules:updated' events to refresh module state
 * - Provides useModules() hook to check if a module is enabled
 * - Provides useIsModuleEnabled(moduleId) hook for specific module checks
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { API_BASE_URL } from '@/base/utils/api';
import type { ModuleNavItem } from '@/base/hooks/useModuleNav';

// ============================================================================
// Types
// ============================================================================

export interface ModuleContextValue {
  /** List of modules from the MMS nav manifest */
  modules: ModuleNavItem[];
  /** Whether modules are currently being loaded */
  isLoading: boolean;
  /** Error message if modules failed to load */
  error: string | null;
  /** Check if a specific module is enabled (any state except UNINSTALLED/DISABLED) */
  isModuleEnabled: (moduleId: string) => boolean;
  /** Check if a module is accessible (can be navigated to) */
  isModuleAccessible: (moduleId: string) => boolean;
  /** Get full module data */
  getModule: (moduleId: string) => ModuleNavItem | undefined;
  /** Manually refresh the modules list */
  refreshModules: () => Promise<void>;
}

// ============================================================================
// Context
// ============================================================================

const ModuleContext = createContext<ModuleContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

export interface ModuleProviderProps {
  children: ReactNode;
}

export function ModuleProvider({ children }: ModuleProviderProps) {
  const [modules, setModules] = useState<ModuleNavItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load modules from MMS nav proxy
  const loadModules = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/v1/modules/nav`, {
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch modules: ${response.status}`);
      }

      const data: { modules: ModuleNavItem[] } = await response.json();
      setModules(data.modules || []);
    } catch (err) {
      console.error('Failed to load modules:', err);
      setError(err instanceof Error ? err.message : 'Failed to load modules');
      // On error, set empty array - all modules will appear disabled
      setModules([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadModules();
  }, [loadModules]);

  // Listen for module toggle events to refresh the module list
  useEffect(() => {
    const handleModuleUpdate = () => {
      loadModules();
    };

    window.addEventListener('ems:modules:updated', handleModuleUpdate);
    return () => {
      window.removeEventListener('ems:modules:updated', handleModuleUpdate);
    };
  }, [loadModules]);

  // Check if a specific module is enabled (not UNINSTALLED or DISABLED)
  const isModuleEnabled = useCallback(
    (moduleId: string): boolean => {
      const module = modules.find((mod) => mod.id === moduleId);
      if (!module) return false;
      return module.state !== 'UNINSTALLED' && module.state !== 'DISABLED';
    },
    [modules]
  );

  // Check if a module is accessible (user can navigate to it)
  const isModuleAccessible = useCallback(
    (moduleId: string): boolean => {
      const module = modules.find((mod) => mod.id === moduleId);
      if (!module) return false;

      const accessibleStates: ModuleNavItem['state'][] = ['ENABLED', 'CONFIGURED', 'ACTIVE'];
      return accessibleStates.includes(module.state) && module.entitled;
    },
    [modules]
  );

  // Get full module data
  const getModule = useCallback(
    (moduleId: string): ModuleNavItem | undefined => {
      return modules.find((mod) => mod.id === moduleId);
    },
    [modules]
  );

  const value: ModuleContextValue = useMemo(
    () => ({
      modules,
      isLoading,
      error,
      isModuleEnabled,
      isModuleAccessible,
      getModule,
      refreshModules: loadModules,
    }),
    [modules, isLoading, error, isModuleEnabled, isModuleAccessible, getModule, loadModules]
  );

  return (
    <ModuleContext.Provider value={value}>
      {children}
    </ModuleContext.Provider>
  );
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to access the module context.
 * Throws an error if used outside of ModuleProvider.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useModules(): ModuleContextValue {
  const context = useContext(ModuleContext);
  if (!context) {
    throw new Error('useModules must be used within a ModuleProvider');
  }
  return context;
}

/**
 * Hook to check if a specific module is enabled.
 * Convenience hook that returns just the enabled status for a single module.
 *
 * @param moduleId - The ID of the module to check
 * @returns boolean indicating if the module is enabled
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useIsModuleEnabled(moduleId: string): boolean {
  const { isModuleEnabled } = useModules();
  return isModuleEnabled(moduleId);
}

/**
 * Hook to check if a specific module is accessible.
 * Convenience hook that returns just the accessibility status for a single module.
 *
 * @param moduleId - The ID of the module to check
 * @returns boolean indicating if the module is accessible
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useIsModuleAccessible(moduleId: string): boolean {
  const { isModuleAccessible } = useModules();
  return isModuleAccessible(moduleId);
}

/**
 * Hook to get full module data.
 * Convenience hook that returns the module data for a single module.
 *
 * @param moduleId - The ID of the module to get
 * @returns ModuleNavItem or undefined if not found
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useModule(moduleId: string): ModuleNavItem | undefined {
  const { getModule } = useModules();
  return getModule(moduleId);
}

/**
 * Optional hook that returns null if used outside of ModuleProvider.
 * Useful for components that may or may not be wrapped in ModuleProvider.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useModulesOptional(): ModuleContextValue | null {
  return useContext(ModuleContext);
}

export { ModuleContext };
export default ModuleProvider;
